/**
 * ServiceAuthorityBlock.tsx — Wave 82
 *
 * Two blocks mounted at the bottom of every service/document page:
 *  1) "แหล่งอ้างอิงอย่างเป็นทางการ" — outbound citations to the government /
 *     treaty bodies that actually govern each document family (E-E-A-T).
 *  2) "บริการที่เกี่ยวข้อง" — internal forward edges to sibling service hubs.
 *
 * No prices, no Review/AggregateRating schema. External links open in a new
 * tab with rel="noopener noreferrer" (dofollow — they are genuine citations).
 */
import { Link } from "@tanstack/react-router";
import { ArrowRight, ExternalLink } from "lucide-react";

export type AuthorityTopic =
  | "general"
  | "vehicle"
  | "tax"
  | "court"
  | "employment"
  | "civil"
  | "company"
  | "property"
  | "education"
  | "medical"
  | "finance"
  | "marriage"
  | "child"
  | "estate"
  | "moj"
  | "police"
  | "sworn"
  | "visa"
  | "naati"
  | "mfa"
  | "notary";

type Src = { href: string; th: string; en: string; note?: string; noteEn?: string };

const BASE: Src[] = [
  {
    href: "https://consular.mfa.go.th",
    th: "กรมการกงสุล กระทรวงการต่างประเทศ",
    en: "Department of Consular Affairs, MFA Thailand",
    note: "หน่วยงานรับรองนิติกรณ์เอกสาร (Legalization) และ Apostille ของไทย",
    noteEn: "Thailand's authority for document legalization and Apostille",
  },
  {
    href: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille",
    th: "HCCH — Apostille Section (อนุสัญญากรุงเฮก ค.ศ. 1961)",
    en: "HCCH — Apostille Section (Hague Convention 1961)",
    note: "ประเทศไทยเข้าเป็นภาคี มีผลใช้บังคับ 28 กุมภาพันธ์ 2570 (2027)",
    noteEn: "Thailand's accession enters into force 28 February 2027",
  },
];

const TOPIC_SOURCES: Record<AuthorityTopic, Src[]> = {
  general: [],
  vehicle: [
    { href: "https://www.dlt.go.th", th: "กรมการขนส่งทางบก", en: "Department of Land Transport" },
    { href: "https://www.customs.go.th", th: "กรมศุลกากร (นำเข้า–ส่งออกยานพาหนะ)", en: "Thai Customs Department" },
  ],
  tax: [
    { href: "https://www.rd.go.th", th: "กรมสรรพากร", en: "The Revenue Department" },
    { href: "https://www.customs.go.th", th: "กรมศุลกากร", en: "Thai Customs Department" },
  ],
  court: [
    { href: "https://www.coj.go.th", th: "สำนักงานศาลยุติธรรม", en: "Office of the Judiciary (Courts of Justice)" },
    { href: "https://www.lawyerscouncil.or.th", th: "สภาทนายความในพระบรมราชูปถัมภ์", en: "Lawyers Council of Thailand" },
  ],
  employment: [
    { href: "https://www.doe.go.th", th: "กรมการจัดหางาน (ใบอนุญาตทำงาน)", en: "Department of Employment (work permits)" },
    { href: "https://www.labour.go.th", th: "กระทรวงแรงงาน", en: "Ministry of Labour" },
  ],
  civil: [
    { href: "https://www.bora.dopa.go.th", th: "สำนักบริหารการทะเบียน กรมการปกครอง", en: "Bureau of Registration Administration, DOPA" },
  ],
  company: [
    { href: "https://www.dbd.go.th", th: "กรมพัฒนาธุรกิจการค้า (DBD)", en: "Department of Business Development (DBD)" },
    { href: "https://www.boi.go.th", th: "สำนักงานคณะกรรมการส่งเสริมการลงทุน (BOI)", en: "Thailand Board of Investment (BOI)" },
  ],
  property: [
    { href: "https://www.dol.go.th", th: "กรมที่ดิน", en: "Department of Lands" },
  ],
  education: [
    { href: "https://www.mhesi.go.th", th: "กระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัยและนวัตกรรม", en: "Ministry of Higher Education, Science, Research and Innovation" },
    { href: "https://www.moe.go.th", th: "กระทรวงศึกษาธิการ", en: "Ministry of Education" },
  ],
  medical: [
    { href: "https://www.tmc.or.th", th: "แพทยสภา", en: "The Medical Council of Thailand" },
    { href: "https://www.moph.go.th", th: "กระทรวงสาธารณสุข", en: "Ministry of Public Health" },
  ],
  finance: [
    { href: "https://www.bot.or.th", th: "ธนาคารแห่งประเทศไทย", en: "Bank of Thailand" },
    { href: "https://www.sec.or.th", th: "สำนักงาน ก.ล.ต.", en: "Securities and Exchange Commission (SEC)" },
  ],
  marriage: [
    { href: "https://www.bora.dopa.go.th", th: "สำนักบริหารการทะเบียน (ทะเบียนสมรส)", en: "Bureau of Registration Administration (marriage registry)" },
  ],
  child: [
    { href: "https://www.bora.dopa.go.th", th: "สำนักบริหารการทะเบียน (สูติบัตร/ทะเบียนบ้าน)", en: "Bureau of Registration Administration (birth records)" },
    { href: "https://www.dcy.go.th", th: "กรมกิจการเด็กและเยาวชน (รับบุตรบุญธรรม)", en: "Department of Children and Youth (adoption)" },
  ],
  estate: [
    { href: "https://www.coj.go.th", th: "สำนักงานศาลยุติธรรม (คดีจัดการมรดก)", en: "Courts of Justice (probate matters)" },
    { href: "https://www.bora.dopa.go.th", th: "สำนักบริหารการทะเบียน (มรณบัตร)", en: "Bureau of Registration Administration (death records)" },
  ],
  moj: [
    { href: "https://www.moj.go.th", th: "กระทรวงยุติธรรม", en: "Ministry of Justice, Thailand" },
  ],
  police: [
    { href: "https://pcscenter.sanook.police.go.th", th: "ศูนย์บริการตรวจสอบประวัติบุคคล สำนักงานตำรวจแห่งชาติ", en: "Police Clearance Service Center, Royal Thai Police" },
  ],
  sworn: [
    { href: "https://www.lawyerscouncil.or.th", th: "สภาทนายความในพระบรมราชูปถัมภ์", en: "Lawyers Council of Thailand" },
  ],
  visa: [
    { href: "https://www.immigration.go.th", th: "สำนักงานตรวจคนเข้าเมือง", en: "Thai Immigration Bureau" },
    { href: "https://www.thaievisa.go.th", th: "ระบบ Thai e-Visa กระทรวงการต่างประเทศ", en: "Thai e-Visa (MFA)" },
  ],
  naati: [
    { href: "https://www.naati.com.au", th: "NAATI — องค์กรรับรองนักแปล/ล่ามของออสเตรเลีย", en: "NAATI — National Accreditation Authority for Translators and Interpreters" },
    { href: "https://immi.homeaffairs.gov.au", th: "Department of Home Affairs (ออสเตรเลีย)", en: "Australian Department of Home Affairs" },
  ],
  mfa: [],
  notary: [
    { href: "https://www.lawyerscouncil.or.th", th: "สภาทนายความ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney)", en: "Lawyers Council of Thailand — Notarial Services Attorney" },
  ],
};

type Rel = { to: string; th: string; en: string };

const RELATED: Rel[] = [
  { to: "/notary", th: "รับรองเอกสารโดยทนาย (Notary Public)", en: "Notarial Services Attorney" },
  { to: "/translate", th: "แปลเอกสารรับรอง (MoJ / MFA / Sworn)", en: "Certified Translation (MoJ / MFA / Sworn)" },
  { to: "/apostille", th: "Apostille (อนุสัญญากรุงเฮก)", en: "Apostille (Hague Convention)" },
  { to: "/embassy", th: "รับรองเอกสารสถานทูต (Legalization)", en: "Embassy Legalization" },
  { to: "/translate/naati", th: "แปลรับรอง NAATI (ออสเตรเลีย)", en: "NAATI Certified Translation" },
  { to: "/visa", th: "บริการวีซ่าและเอกสารประกอบ", en: "Visa Services & Supporting Documents" },
  { to: "/police-clearance", th: "หนังสือรับรองความประพฤติ", en: "Police Clearance Certificate" },
  { to: "/quote", th: "ขอใบเสนอราคา / ติดต่อทีมงาน (LINE · โทร · อีเมล)", en: "Request a quotation (LINE · phone · email)" },
];

function localized(to: string, locale: "th" | "en") {
  return locale === "en" ? `/en${to}` : to;
}

export function ServiceAuthorityBlock({
  topic = "general",
  locale = "th",
}: {
  topic?: AuthorityTopic;
  locale?: "th" | "en";
}) {
  const en = locale === "en";
  const sources = [...(TOPIC_SOURCES[topic] ?? []), ...BASE];

  return (
    <section className="mt-12 space-y-10" data-track-zone="authority-block">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">
          {en ? "Official references" : "แหล่งอ้างอิงอย่างเป็นทางการ"}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {en
            ? "Requirements on this page follow the rules published by the authorities below. Always confirm the latest notice on the official site before filing."
            : "ข้อกำหนดในหน้านี้อ้างอิงประกาศของหน่วยงานด้านล่าง โปรดตรวจสอบประกาศล่าสุดจากเว็บไซต์ทางการก่อนยื่นทุกครั้ง"}
        </p>
        <ul className="mt-3 space-y-2 text-sm leading-7">
          {sources.map((s) => (
            <li key={s.href}>
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
              >
                {en ? s.en : s.th}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              {(en ? s.noteEn : s.note) && (
                <span className="block text-muted-foreground">{en ? s.noteEn : s.note}</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h2 className="text-xl font-semibold tracking-tight">
          {en ? "Related services" : "บริการที่เกี่ยวข้อง"}
        </h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
          {RELATED.map((r) => (
            <li key={r.to}>
              <Link
                to={localized(r.to, locale)}
                className="group inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                {en ? r.en : r.th}
                <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        {en
          ? "Fees depend on document type, number of pages and turnaround. Send scans to LINE @nycli, call +66-83-249-4999 or email nycli@ilc.ltd for a written quotation."
          : "ค่าบริการขึ้นอยู่กับชนิดเอกสาร จำนวนหน้า และกรอบเวลา ส่งไฟล์สแกนทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd เพื่อรับใบเสนอราคาเป็นลายลักษณ์อักษร"}
      </p>
    </section>
  );
}

export default ServiceAuthorityBlock;
